import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { requestJson } from '../api/client';
import { ME_QUERY_KEY, useAuthMethods, useCurrentUser } from '../api/session';
import { APP_MARK, APP_NAME } from '../brand';
import { ErrorState, LoadingState } from '../components/states';

interface DevUser {
  id: string;
  displayName: string;
  position: string | null;
  employeeStatus: 'active' | 'dismissed' | null;
}

/** Вход без пароля — только в сборке разработки и только на синтетических данных. */
function DevLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const from = useReturnPath();

  const users = useQuery({
    queryKey: ['dev', 'users'],
    queryFn: () => requestJson<{ items: DevUser[] }>('/api/v1/dev/users'),
  });
  const login = useMutation({
    mutationFn: (userId: string) =>
      requestJson<void>('/api/v1/dev/login', { method: 'POST', body: JSON.stringify({ userId }) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      navigate(from, { replace: true });
    },
  });

  if (users.isPending) return <LoadingState label="Загружаю тестовых пользователей" />;
  if (users.isError) {
    return (
      <ErrorState
        title="Вход для разработки недоступен"
        error={users.error}
        onRetry={() => void users.refetch()}
      />
    );
  }

  return (
    <div className="login-panel">
      <p className="muted">
        Режим разработки: войдите от имени пользователя синтетической организации, чтобы проверить права.
      </p>
      {login.isError ? <ErrorState title="Не удалось войти" error={login.error} /> : null}
      <ul className="login-users">
        {users.data.items.map((user) => (
          <li key={user.id}>
            <button
              type="button"
              className="login-user"
              disabled={login.isPending}
              onClick={() => login.mutate(user.id)}
            >
              <span className="login-user-name">{user.displayName}</span>
              <span className="login-user-meta">
                {user.employeeStatus === 'dismissed'
                  ? 'уволен — вход будет отклонён'
                  : (user.position ?? 'технический пользователь')}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

const LOGIN_ERRORS: Record<string, string> = {
  portal: 'Вход разрешён только из портала Bitrix24 компании.',
  auth: `Bitrix24 не подтвердил вход. Откройте «${APP_NAME}» из левого меню Bitrix24 ещё раз.`,
  state: 'Сеанс входа устарел. Попробуйте войти ещё раз.',
  unknown_employee:
    'Ваша учётная запись ещё не загружена из Bitrix24. Мы уже запустили обновление — попробуйте через несколько минут.',
  dismissed: 'Учётная запись отключена в Bitrix24. Если это ошибка, обратитесь к HR.',
  blocked: 'Доступ заблокирован администратором.',
  not_configured: 'Вход через Bitrix24 на этом сервере ещё не настроен.',
  unavailable: 'Bitrix24 сейчас не отвечает. Попробуйте через минуту.',
};

/** Вход через Bitrix24: из левого меню портала или по кнопке, если настроен OAuth. */
function Bitrix24Login({ returnPath }: { returnPath: string }) {
  const methods = useAuthMethods();
  const location = useLocation();
  const error = new URLSearchParams(location.search).get('error');
  const message = error ? (LOGIN_ERRORS[error] ?? 'Не удалось войти. Попробуйте ещё раз.') : null;

  if (methods.isPending) return <LoadingState label="Проверяю способы входа" />;
  if (!methods.data) {
    return message ? <ErrorState title="Не удалось войти" error={new Error(message)} /> : null;
  }

  const { portal, oauth } = methods.data.bitrix24;
  return (
    <div className="login-panel">
      {message ? (
        <p className="login-error" role="alert">
          {message}
        </p>
      ) : null}
      <p className="muted">
        Откройте «{APP_NAME}» из левого меню Bitrix24 — вход произойдёт автоматически, без пароля.
      </p>
      {oauth ? (
        <a
          className="btn-primary"
          href={`/api/v1/auth/bitrix24/start?return=${encodeURIComponent(returnPath)}`}
        >
          Войти через Bitrix24
        </a>
      ) : (
        <a className="btn-primary" href={`https://${portal}/`} target="_top">
          Открыть Bitrix24
        </a>
      )}
    </div>
  );
}

/** Куда вернуть пользователя после входа: на страницу, с которой его перенаправили. */
function useReturnPath(): string {
  const location = useLocation();
  return (location.state as { from?: string } | null)?.from ?? '/org';
}

export function LoginPage() {
  const me = useCurrentUser();
  const returnPath = useReturnPath();
  // Тот же адрес, что и после успешного входа: иначе два перехода соревнуются.
  if (me.data) return <Navigate to={returnPath} replace />;

  return (
    <main className="login">
      <section className="panel login-card" aria-labelledby="page-title">
        <div className="brand" style={{ paddingLeft: 0 }}>
          <span className="brand-mark" aria-hidden="true">
            {APP_MARK}
          </span>
          <span className="brand-name">{APP_NAME}</span>
        </div>
        <h1 id="page-title">Вход</h1>
        <Bitrix24Login returnPath={returnPath} />
        {import.meta.env.DEV ? <DevLogin /> : null}
      </section>
    </main>
  );
}
